import CreatableSelect from "react-select/creatable";
import * as ReactSelect from "react-select";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { OptionType } from "@core/types/select.types";

// Architecture Note: This Select intentionally does NOT wrap SelectBase.
// Reason: This component uses react-select library for advanced features
// (searchable, multi-select, creatable options) that are not available in
// a native HTML select element. SelectBase is designed for simple native
// select elements with basic options.
// They serve different purposes:
// - Select (this): Advanced select with search, multi-select, create options
// - SelectBase: Simple native HTML select element

// Re-export OptionType for backwards compatibility
export type { OptionType } from "@core/types/select.types";

export type Props = {
  /** Field name for identification */
  name: string;
  /** Available options */
  options: OptionType[];
  /** Currently selected value(s) */
  value?: OptionType | OptionType[] | null;
  /** Change handler - receives selected option(s) */
  onChange?: (val: OptionType | OptionType[] | null) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether multiple options can be selected */
  isMulti?: boolean;
  /** Whether new options can be created */
  isCreatable?: boolean;
  /** Handler for creating new options */
  onCreate?: (input: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the select is loading */
  isLoading?: boolean;
  /** Whether field has an icon (affects padding) */
  isWithIcon?: boolean;
  /** Size variant */
  size?: "default" | "lg";
  /** Additional CSS classes */
  className?: string;
  /** ID of the element that labels this select (for accessibility) */
  "aria-labelledby"?: string;
};

/**
 * Styled select component using react-select library.
 * Pure UI component - works with or without react-hook-form.
 *
 * Features: searchable, multi-select, creatable options.
 * For a simple native HTML select, use SelectBase from @core/ui.
 *
 * @example
 * // Single select
 * <Select
 *   name="country"
 *   options={countries}
 *   value={selectedCountry}
 *   onChange={setSelectedCountry}
 * />
 *
 * @example
 * // Multi-select
 * <Select
 *   name="tags"
 *   options={tags}
 *   value={selectedTags}
 *   onChange={setSelectedTags}
 *   isMulti
 * />
 *
 * @example
 * // Creatable (allows adding new options)
 * <Select
 *   name="category"
 *   options={categories}
 *   value={selectedCategory}
 *   onChange={setSelectedCategory}
 *   isCreatable
 * />
 */
export const Select = forwardRef<any, Props>(
  (
    {
      name,
      options,
      value,
      onChange,
      onBlur,
      error,
      disabled = false,
      isMulti = false,
      isCreatable = false,
      onCreate,
      placeholder,
      isLoading = false,
      isWithIcon = false,
      size = "default",
      className,
      "aria-labelledby": ariaLabelledBy,
    },
    ref
  ) => {
    const SelectComponent = isCreatable ? CreatableSelect : ReactSelect.default;

    const handleChange = (selected: any) => {
      onChange?.(selected ?? null);
    };

    const handleCreate = (input: string) => {
      onCreate?.(input);

      const newOption: OptionType = {
        label: input,
        value: input.toLowerCase().replace(/\s+/g, "-"),
      };

      if (isMulti) {
        const current = Array.isArray(value) ? value : [];
        onChange?.([...current, newOption]);
      } else {
        onChange?.(newOption);
      }
    };

    return (
      <div
        className={twMerge(
          "border-none shadow-none",
          error && "ring-2 ring-error-600 rounded-sm",
          className
        )}
      >
        <SelectComponent
          ref={ref}
          data-testid={`select-${name}`}
          unstyled
          inputId={name}
          isDisabled={disabled}
          isLoading={isLoading}
          isMulti={isMulti}
          options={options}
          value={value ?? null}
          onChange={handleChange}
          onCreateOption={isCreatable ? handleCreate : undefined}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          onBlur={onBlur}
          aria-labelledby={ariaLabelledBy}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          classNames={{
            container: ({ isDisabled }) =>
              twMerge(
                "transition-all duration-200 relative w-full drop-shadow-lg",
                isDisabled &&
                  "rounded-sm bg-brand-300 cursor-not-allowed drop-shadow-none"
              ),
            control: ({ isDisabled, isFocused }) =>
              twMerge(
                "transition-all duration-200 w-full border flex w-full rounded-sm border-brand-800 bg-transparent shadow-sm transition-colors",
                size === "lg" ? "h-12 px-4 py-3 text-base" : "h-10 px-3 py-2 text-sm",
                isFocused && "border-2 border-brand-600",
                isDisabled && "border-brand-300"
              ),
            input: () => (isWithIcon ? "pl-6" : ""),
            menu: () =>
              "relative flex flex-col border-2 border-brand-600 rounded-sm mt-2 mb-8 bg-brand-50 overflow-y-auto max-h-[300px]",
            option: ({ isFocused, isSelected, data }) =>
              twMerge(
                "transition-all duration-200 px-4 py-2 w-full",
                isFocused && "bg-brand-100",
                isSelected && "bg-brand-100",
                data?.__isNew__ &&
                  "text-brand-600 font-medium before:content-['+'] before:mr-2"
              ),
            noOptionsMessage: () =>
              "flex px-4 py-2 w-full text-left text-brand-400 text-sm",
            placeholder: () =>
              twMerge(
                "transition-all duration-200 text-brand-400 truncate",
                isWithIcon && "pl-6"
              ),
            singleValue: () => twMerge("truncate", isWithIcon && "pl-6"),
            loadingMessage: () =>
              "px-4 py-2 w-full text-left text-brand-400 text-sm",
            valueContainer: () => "flex flex-1 items-center gap-2",
            indicatorsContainer: ({ isDisabled }) =>
              twMerge(
                "transition-all duration-200 flex items-center gap-1",
                isDisabled && "text-brand-400"
              ),
            indicatorSeparator: () => "hidden",
          }}
        />
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
